import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const DURUM_RENK = {
  "BEKLIYOR": "warning",
  "CEVAPLANDI": "success",
  "REDDEDILDI": "danger",
  "GIZLENDI": "secondary",
};

const MagazaSorulari = () => {
  document.title = "Mağaza Soruları | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(null);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/MAGAZA_SORULAR?$orderby=ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Mağaza soruları yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAnswer = (row) => {
    setFormData({
      ID: row.ID,
      MAGAZA_ID: row.MAGAZA_ID,
      SORAN_ID: row.SORAN_ID,
      SORU_METNI: row.SORU_METNI || "",
      CEVAP_METNI: row.CEVAP_METNI || "",
      DURUM: row.DURUM || "BEKLIYOR",
    });
    setModal(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        CEVAP_METNI: formData.CEVAP_METNI || null,
        DURUM: formData.DURUM,
      };
      await fetch(`${BASE_URL}/MAGAZA_SORULAR(${formData.ID})`, {
        method: "PATCH", headers, body: JSON.stringify(payload),
      });
      setSuccessMsg("Soru güncellendi.");
      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu soruyu silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/MAGAZA_SORULAR(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Soru silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Mağaza",
      accessorKey: "MAGAZA_ID",
      enableSorting: true,
      cell: (c) => <Badge color="light" className="text-dark">Mağaza #{c.getValue()}</Badge>,
    },
    {
      header: "Soran",
      accessorKey: "SORAN_ID",
      enableSorting: true,
      cell: (c) => <small className="text-muted">Kullanıcı #{c.getValue()}</small>,
    },
    {
      header: "Soru",
      accessorKey: "SORU_METNI",
      enableSorting: false,
      cell: (c) => {
        const v = c.getValue() || "";
        return <span className="fw-medium">{v.length > 70 ? v.slice(0, 70) + "…" : (v || "---")}</span>;
      },
    },
    {
      header: "Cevap",
      accessorKey: "CEVAP_METNI",
      enableSorting: false,
      cell: (c) => {
        const v = c.getValue() || "";
        return v
          ? <span className="text-muted">{v.length > 50 ? v.slice(0, 50) + "…" : v}</span>
          : <span className="text-warning"><i className="bx bx-time" /> Cevaplanmadı</span>;
      },
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      cell: (c) => {
        const v = c.getValue() || "BEKLIYOR";
        return <Badge color={DURUM_RENK[v] || "info"}>{v}</Badge>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => openAnswer(row)}>
              <i className="bx bx-message-rounded-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İlan Yönetimi" breadcrumbItem="Mağaza Soruları" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <h4 className="card-title mb-1">Mağaza Soru & Cevapları</h4>
                    <p className="text-muted small mb-0">
                      Kullanıcıların mağazalara sorduğu soruları görüntüleyin, cevaplayın veya durumlarını yönetin.
                    </p>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Soru ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className="mdi mdi-message-reply-text-outline me-2 text-primary"></i>
          Soru Yönetimi {formData && `(#${formData.ID})`}
        </ModalHeader>
        <ModalBody>
          {formData && (
            <Row>
              <Col md={12}>
                <div className="bg-light rounded p-3 mb-3">
                  <small className="text-muted d-block mb-1">
                    Mağaza #{formData.MAGAZA_ID} · Soran: Kullanıcı #{formData.SORAN_ID}
                  </small>
                  <strong>Soru:</strong>
                  <p className="mb-0">{formData.SORU_METNI || "—"}</p>
                </div>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label className="fw-bold">Cevap</Label>
                  <Input
                    type="textarea"
                    rows={5}
                    value={formData.CEVAP_METNI}
                    onChange={(e) => f("CEVAP_METNI", e.target.value)}
                    placeholder="Cevabı yazın..."
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label className="fw-bold">Durum</Label>
                  <Input type="select" value={formData.DURUM} onChange={(e) => f("DURUM", e.target.value)}>
                    <option value="BEKLIYOR">Bekliyor</option>
                    <option value="CEVAPLANDI">Cevaplandı</option>
                    <option value="REDDEDILDI">Reddedildi</option>
                    <option value="GIZLENDI">Gizlendi</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            Kaydet
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default MagazaSorulari;
